import { useMemo } from "react";
import {
  getProductInteractivityTypes,
  isProductOnSale,
} from "../../../helper/utility";

/**
 * Compute live filter counts for the bottom-sheet filter UI.
 *
 * The combined filter logic is:
 *  - AND between category, name, tag, promo, brand, on-sale, interactivity
 *  - OR within each multi-select group (tag/promo/brand/interactivity match any id)
 *
 * For performance, we precompute the list of products that already pass the
 * (immutable for this call) category + name filters, then for each "would I
 * select this chip?" check we only evaluate the remaining group filters.
 *
 * @param {Object} args
 * @param {Array} args.products
 * @param {string} args.categoryFilter
 * @param {string} args.nameFilter
 * @param {Set<number|string>} args.pendingTags
 * @param {Set<number|string>} args.pendingPromos
 * @param {Set<string>} args.pendingBrands
 * @param {boolean} args.pendingOnSale
 * @param {Set<string>} args.pendingInteractivities
 * @returns {{
 *   totalCount: number,
 *   getTagCount: (id: number|string) => number,
 *   getPromoCount: (id: number|string) => number,
 *   getBrandCount: (name: string) => number,
 *   getOnSaleCount: () => number,
 *   getInteractivityCount: (type: string) => number,
 * }}
 */
export default function useFilterCounts({
  products,
  categoryFilter,
  nameFilter,
  pendingTags,
  pendingPromos,
  pendingBrands,
  pendingOnSale = false,
  pendingInteractivities,
}) {
  return useMemo(() => {
    const safeProducts = Array.isArray(products) ? products : [];
    const nameFilterTrim = (nameFilter || "").trim().toLowerCase();
    const useName = nameFilterTrim.length > 2;
    const catLower = (categoryFilter || "").toLowerCase();

    const matchCategoryAndName = (p) => {
      if (catLower) {
        const pc = (p.category || "").toLowerCase();
        if (pc !== catLower) return false;
      }
      if (useName) {
        const desc = String(p.description || "").toLowerCase();
        if (!desc.includes(nameFilterTrim)) return false;
      }
      return true;
    };

    const baseFiltered = safeProducts.filter(matchCategoryAndName);

    const matchTags = (p, tagSet) => {
      if (!tagSet || tagSet.size === 0) return true;
      if (!Array.isArray(p.tags)) return false;
      return p.tags.some((t) => tagSet.has(t));
    };

    const matchPromos = (p, promoSet) => {
      if (!promoSet || promoSet.size === 0) return true;
      const promoMap = p.attributes && p.attributes.promo;
      if (!promoMap) return false;
      return Object.entries(promoMap).some(([parentId, childId]) => {
        if (promoSet.has(Number(parentId))) return true;
        if (childId && promoSet.has(childId)) return true;
        return false;
      });
    };

    const matchBrands = (p, brandSet) => {
      if (!brandSet || brandSet.size === 0) return true;
      return brandSet.has(p.brand);
    };

    const matchOnSale = (p, onSale) => (onSale ? isProductOnSale(p) : true);

    const matchInteractivities = (p, interSet) => {
      if (!interSet || interSet.size === 0) return true;
      const types = getProductInteractivityTypes(p);
      for (const t of interSet) {
        if (types.has(t)) return true;
      }
      return false;
    };

    const countWith = (tagSet, promoSet, brandSet, onSale, interSet) => {
      let n = 0;
      for (let i = 0; i < baseFiltered.length; i++) {
        const p = baseFiltered[i];
        if (
          matchTags(p, tagSet) &&
          matchPromos(p, promoSet) &&
          matchBrands(p, brandSet) &&
          matchOnSale(p, onSale) &&
          matchInteractivities(p, interSet)
        ) {
          n++;
        }
      }
      return n;
    };

    const interSet = pendingInteractivities || new Set();

    const totalCount = countWith(
      pendingTags,
      pendingPromos,
      pendingBrands,
      pendingOnSale,
      interSet
    );

    const getTagCount = (id) =>
      countWith(
        new Set([id]),
        pendingPromos,
        pendingBrands,
        pendingOnSale,
        interSet
      );

    const getPromoCount = (id) =>
      countWith(
        pendingTags,
        new Set([id]),
        pendingBrands,
        pendingOnSale,
        interSet
      );

    const getBrandCount = (name) =>
      countWith(
        pendingTags,
        pendingPromos,
        new Set([name]),
        pendingOnSale,
        interSet
      );

    const getOnSaleCount = () =>
      countWith(pendingTags, pendingPromos, pendingBrands, true, interSet);

    const getInteractivityCount = (type) =>
      countWith(
        pendingTags,
        pendingPromos,
        pendingBrands,
        pendingOnSale,
        new Set([type])
      );

    return {
      totalCount,
      getTagCount,
      getPromoCount,
      getBrandCount,
      getOnSaleCount,
      getInteractivityCount,
    };
  }, [
    products,
    categoryFilter,
    nameFilter,
    pendingTags,
    pendingPromos,
    pendingBrands,
    pendingOnSale,
    pendingInteractivities,
  ]);
}
